/**
 * EpisodeOfCareManager Component
 * 
 * Displays and manages oncology episodes of care for a patient.
 * Shows all episodes, allows creating new ones, and navigating to episode details.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Timeline as TimelineIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  LocalHospital as HospitalIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  CheckCircle as CheckIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Pause as PauseIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useUserData } from '../../hooks/useUserData';
import {
  getPatientEpisodes,
  createEpisode,
  updateEpisode,
  EPISODE_STATUS,
  EPISODE_PHASE,
  EPISODE_OUTCOME
} from '../../services/episodeOfCareService';
import { getCancerDiagnoses as getPatientDiagnoses } from '../../services/oncologyService';

const statusConfig = {
  'Active': { color: 'success', icon: <CheckIcon fontSize="small" /> },
  'On Hold': { color: 'warning', icon: <PauseIcon fontSize="small" /> },
  'Completed': { color: 'info', icon: <CheckIcon fontSize="small" /> },
  'Cancelled': { color: 'error', icon: <CancelIcon fontSize="small" /> }
};

const phaseConfig = {
  'Diagnosis': { color: 'default' },
  'Active Treatment': { color: 'primary' },
  'Surveillance': { color: 'info' },
  'Survivorship': { color: 'success' },
  'Palliative': { color: 'warning' },
  'End of Life': { color: 'error' }
};

const EpisodeOfCareManager = ({
  patientId,
  patient,
  onSelectEpisode,
  showCreateButton = true,
  compact = false
}) => {
  const { defaultFacility } = useUserData();
  const facilityId = defaultFacility?.id;

  // State
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [diagnoses, setDiagnoses] = useState([]);
  const [loadingDiagnoses, setLoadingDiagnoses] = useState(false);
  const [formData, setFormData] = useState({
    diagnosisId: '',
    episodeName: '',
    currentPhase: 'Diagnosis',
    primaryOncologist: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  // Fetch episodes
  const fetchEpisodes = useCallback(async () => {
    if (!patientId || !facilityId) return;

    try {
      setLoading(true);
      setError(null);
      const result = await getPatientEpisodes(patientId, facilityId);
      setEpisodes(result.data || []);
    } catch (err) {
      console.error('Error fetching episodes:', err);
      setError('Failed to load episodes of care');
    } finally {
      setLoading(false);
    }
  }, [patientId, facilityId]);

  // Fetch diagnoses for dropdown
  const fetchDiagnoses = useCallback(async () => {
    if (!patientId || !facilityId) return;

    try {
      setLoadingDiagnoses(true);
      const result = await getPatientDiagnoses(patientId, facilityId);
      // Filter out diagnoses that already have an episode
      const availableDiagnoses = (result.data || []).filter(
        d => !d.episodeOfCareId
      );
      setDiagnoses(availableDiagnoses);
    } catch (err) {
      console.error('Error fetching diagnoses:', err);
    } finally {
      setLoadingDiagnoses(false);
    }
  }, [patientId, facilityId]);

  useEffect(() => {
    fetchEpisodes();
  }, [fetchEpisodes]);

  // Handle opening create dialog
  const handleOpenCreateDialog = () => {
    fetchDiagnoses();
    setFormData({
      diagnosisId: '',
      episodeName: '',
      currentPhase: 'Diagnosis',
      primaryOncologist: '',
      notes: ''
    });
    setCreateDialogOpen(true);
  };

  // Handle opening edit dialog
  const handleOpenEditDialog = (episode) => {
    setSelectedEpisode(episode);
    setFormData({
      diagnosisId: episode.diagnosisId || '',
      episodeName: episode.episodeName || '',
      currentPhase: episode.currentPhase || 'Diagnosis',
      status: episode.status || 'Active',
      outcome: episode.outcome || '',
      primaryOncologist: episode.primaryOncologist || '',
      notes: episode.notes || ''
    });
    setEditDialogOpen(true);
  };

  // Handle form change
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle create episode
  const handleCreateEpisode = async () => {
    if (!formData.diagnosisId) {
      setError('Please select a cancer diagnosis');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const selectedDiagnosis = diagnoses.find(d => d.id === formData.diagnosisId);
      const episodeName = formData.episodeName || 
        `${selectedDiagnosis?.cancerType || 'Cancer'} Treatment ${new Date().getFullYear()}`;

      await createEpisode({
        patientId,
        facilityId,
        diagnosisId: formData.diagnosisId,
        episodeName,
        currentPhase: formData.currentPhase,
        primaryOncologist: formData.primaryOncologist,
        notes: formData.notes
      });

      setCreateDialogOpen(false);
      fetchEpisodes();
    } catch (err) {
      console.error('Error creating episode:', err);
      setError('Failed to create episode of care');
    } finally {
      setSaving(false);
    }
  };

  // Handle update episode
  const handleUpdateEpisode = async () => {
    if (!selectedEpisode) return;

    try {
      setSaving(true);
      setError(null);

      await updateEpisode(selectedEpisode.id, {
        episodeName: formData.episodeName,
        currentPhase: formData.currentPhase,
        status: formData.status,
        outcome: formData.outcome,
        primaryOncologist: formData.primaryOncologist,
        notes: formData.notes,
        endDate: formData.status === 'Completed' ? new Date().toISOString() : null
      });

      setEditDialogOpen(false);
      setSelectedEpisode(null);
      fetchEpisodes();
    } catch (err) {
      console.error('Error updating episode:', err);
      setError('Failed to update episode of care');
    } finally {
      setSaving(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimelineIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Episodes of Care
          </Typography>
          <Chip 
            label={episodes.length} 
            size="small" 
            color="primary" 
            variant="outlined"
          />
        </Box>
        {showCreateButton && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            New Episode
          </Button>
        )}
      </Box>

      {/* Episodes List */}
      {episodes.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <TimelineIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No episodes of care found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create an episode when a cancer diagnosis is made to track the patient's treatment journey.
            </Typography>
            {showCreateButton && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateDialog}
              >
                Create First Episode
              </Button>
            )}
          </CardContent>
        </Card>
      ) : compact ? (
        // Compact view - cards
        <Grid container spacing={2}>
          {episodes.map((episode) => (
            <Grid item xs={12} md={6} key={episode.id}>
              <Card 
                variant="outlined" 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main', boxShadow: 1 }
                }}
                onClick={() => onSelectEpisode?.(episode)}
              >
                <CardContent sx={{ pb: '12px !important' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {episode.episodeName || 'Unnamed Episode'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {episode.diagnosisSummary || 'Cancer diagnosis'}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={episode.status}
                      color={statusConfig[episode.status]?.color || 'default'}
                      icon={statusConfig[episode.status]?.icon}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Chip
                      size="small"
                      label={episode.currentPhase}
                      color={phaseConfig[episode.currentPhase]?.color || 'default'}
                      variant="outlined"
                    />
                    <Typography variant="caption" color="text.secondary">
                      <CalendarIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                      Started: {formatDate(episode.startDate)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        // Table view
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 600 }}>Episode</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Diagnosis</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Phase</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Encounters</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {episodes.map((episode) => (
                <TableRow 
                  key={episode.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onSelectEpisode?.(episode)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {episode.episodeName || 'Unnamed Episode'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {episode.diagnosisSummary || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={episode.currentPhase}
                      color={phaseConfig[episode.currentPhase]?.color || 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={episode.status}
                      color={statusConfig[episode.status]?.color || 'default'}
                    />
                  </TableCell>
                  <TableCell>{formatDate(episode.startDate)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={episode.encounterIds?.length || 0}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Timeline">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectEpisode?.(episode);
                        }}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Episode">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditDialog(episode);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Episode Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon color="primary" />
            Create Episode of Care
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Cancer Diagnosis</InputLabel>
              <Select
                value={formData.diagnosisId}
                label="Cancer Diagnosis"
                onChange={(e) => handleFormChange('diagnosisId', e.target.value)}
                disabled={loadingDiagnoses}
              >
                {loadingDiagnoses ? (
                  <MenuItem disabled>Loading...</MenuItem>
                ) : diagnoses.length === 0 ? (
                  <MenuItem disabled>No diagnoses without episodes</MenuItem>
                ) : (
                  diagnoses.map((diagnosis) => (
                    <MenuItem key={diagnosis.id} value={diagnosis.id}>
                      {diagnosis.cancerType} - Stage {diagnosis.staging?.clinicalStage || diagnosis.staging?.pathologicalStage || 'Unknown'}
                      {' ('}{formatDate(diagnosis.diagnosisDate)}{')'}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Episode Name (Optional)"
              value={formData.episodeName}
              onChange={(e) => handleFormChange('episodeName', e.target.value)}
              placeholder="e.g., Breast Cancer Treatment 2024"
              helperText="Auto-generated if not provided"
            />

            <FormControl fullWidth>
              <InputLabel>Current Phase</InputLabel>
              <Select
                value={formData.currentPhase}
                label="Current Phase"
                onChange={(e) => handleFormChange('currentPhase', e.target.value)}
              >
                {Object.values(EPISODE_PHASE).map((phase) => (
                  <MenuItem key={phase} value={phase}>{phase}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => handleFormChange('notes', e.target.value)}
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateEpisode}
            disabled={saving || !formData.diagnosisId}
          >
            {saving ? <CircularProgress size={20} /> : 'Create Episode'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Episode Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon color="primary" />
            Edit Episode of Care
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Episode Name"
              value={formData.episodeName}
              onChange={(e) => handleFormChange('episodeName', e.target.value)}
            />

            <FormControl fullWidth>
              <InputLabel>Current Phase</InputLabel>
              <Select
                value={formData.currentPhase}
                label="Current Phase"
                onChange={(e) => handleFormChange('currentPhase', e.target.value)}
              >
                {Object.values(EPISODE_PHASE).map((phase) => (
                  <MenuItem key={phase} value={phase}>{phase}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => handleFormChange('status', e.target.value)}
              >
                {Object.values(EPISODE_STATUS).map((status) => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {formData.status === 'Completed' && (
              <FormControl fullWidth>
                <InputLabel>Outcome</InputLabel>
                <Select
                  value={formData.outcome}
                  label="Outcome"
                  onChange={(e) => handleFormChange('outcome', e.target.value)}
                >
                  {Object.values(EPISODE_OUTCOME).map((outcome) => (
                    <MenuItem key={outcome} value={outcome}>{outcome}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => handleFormChange('notes', e.target.value)}
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleUpdateEpisode}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EpisodeOfCareManager;
